import { useApp } from "@/App";
import { useEffect, useRef, useState } from "react";
import { HelpCircle, Image as ImageIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BalanceSection from "@/components/BalanceSection";
import QuickActions from "@/components/QuickActions";
import CryptoList from "@/components/CryptoList";
import QrMaintenanceBanner from "@/components/QrMaintenanceBanner";
import { openSupportChat } from "@/lib/support";
import { useQrMaintenance } from "@/lib/qrMaintenance";
import { toast } from "sonner";

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, haptic, telegram } = useApp();
  const qrMaintenance = useQrMaintenance();
  const [avatarError, setAvatarError] = useState(false);
  const galleryInputRef = useRef(null);
  const profilePhotoUrl = String(user?.photo_url || user?.settings?.photo_url || "").trim();

  const handleHomeGallerySelected = (event) => {
    const input = event?.target;
    const file = input?.files?.[0];
    if (input) input.value = "";
    if (!file) return;
    try {
      window.__qrGalleryPendingFile = file;
    } catch (_) {}
    navigate("/scan?pick=gallery");
  };

  const handleGalleryPayClick = () => {
    haptic("light");
    // Paying from a gallery photo lands on the same intent flow as the scanner,
    // so it has to be closed off during maintenance too.
    if (qrMaintenance.active) {
      toast.info(qrMaintenance.short);
      return;
    }
    galleryInputRef.current?.click?.();
  };

  const handleSupportClick = () => {
    haptic("light");
    openSupportChat(telegram);
  };

  const displayName = user?.username || user?.first_name || "user";
  const avatarFallback = (displayName?.[0] || 'U').toUpperCase();

  useEffect(() => {
    setAvatarError(false);
  }, [profilePhotoUrl]);

  return (
    <div className="safe-bottom-home" data-testid="home-page">
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleHomeGallerySelected}
      />
      {/* Шапка. В полноэкранном режиме её верхний отступ считается от чёлки
          и от круглых кнопок Telegram — см. --lp-top в index.css. */}
      <div className="header-section lp-rise lp-rise-1">
        <button
          type="button"
          className={`support-icon-btn ${qrMaintenance.active ? "opacity-40" : ""}`}
          onClick={handleGalleryPayClick}
          aria-disabled={qrMaintenance.active}
          aria-label={t("home.pay_from_gallery", { defaultValue: "Оплата из галереи" })}
          data-testid="home-gallery-pay-btn"
        >
          <ImageIcon className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="user-avatar">
            {profilePhotoUrl && !avatarError ? (
              <img
                src={profilePhotoUrl}
                alt={displayName}
                className="user-avatar-img"
                onError={() => setAvatarError(true)}
              />
            ) : (
              avatarFallback
            )}
          </div>
          <span className="font-semibold text-foreground">
            {displayName}
          </span>
        </div>
        <button
          type="button"
          className="support-icon-btn"
          onClick={handleSupportClick}
          aria-label={t("profile.help_support")}
          data-testid="home-support-btn"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </div>

      <div className="lp-rise lp-rise-2">
        <BalanceSection />
      </div>

      {/* Shown only while QR payments are switched off for maintenance */}
      <QrMaintenanceBanner />

      <div className="lp-rise lp-rise-3">
        <QuickActions />
      </div>

      <div className="lp-rise lp-rise-4">
        <CryptoList />
      </div>

      {/* End spacer removed to avoid extra scroll */}
    </div>
  );
};

export default HomePage;
